import { useState, useEffect } from 'react';
import { apiFetch } from './api';

export default function TeamSquadSettings() {
  const [teams, setTeams] = useState([]);
  const [squads, setSquads] = useState([]);
  const [newValue, setNewValue] = useState('');
  const [newColor, setNewColor] = useState('#3b82f6');
  const [selectedType, setSelectedType] = useState('team');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('teams');

  async function loadData() {
    try {
      setLoading(true);
      setError('');
      const [teamsData, squadsData] = await Promise.all([
        apiFetch('/teams'),
        apiFetch('/squads')
      ]);
      setTeams(teamsData);
      setSquads(squadsData);
    } catch (e) {
      setError(e.message || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  async function addOption() {
    if (!newValue.trim()) {
      alert('Please enter a value');
      return;
    }

    const endpoint = selectedType === 'team' ? '/teams' : '/squads';
    const label = selectedType === 'team' ? 'team' : 'squad';

    try {
      const body = { name: newValue.trim() };
      if (selectedType === 'team' && newColor) {
        body.color = newColor;
      }
      await apiFetch(endpoint, {
        method: 'POST',
        body: JSON.stringify(body)
      });
      setNewValue('');
      setNewColor('#3b82f6');
      await loadData();
    } catch (e) {
      alert(e.message || `Failed to add ${label}`);
    }
  }

  async function updateTeamColor(id, color) {
    try {
      await apiFetch(`/teams/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ color })
      });
      await loadData();
    } catch (e) {
      alert(e.message || 'Failed to update team color');
    }
  }

  async function updateTeamSlackChannel(id, slackChannel) {
    try {
      await apiFetch(`/teams/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ slackChannel })
      });
      await loadData();
    } catch (e) {
      alert(e.message || 'Failed to update team Slack channel');
    }
  }

  async function deleteOption(id, type, name) {
    const endpoint = type === 'team' ? `/teams/${id}` : `/squads/${id}`;
    const label = type === 'team' ? 'team' : 'squad';

    const confirmed = window.confirm(`Delete ${label} "${name}"?`);
    if (!confirmed) return;

    try {
      await apiFetch(endpoint, {
        method: 'DELETE'
      });
      await loadData();
    } catch (e) {
      alert(e.message || `Failed to delete ${label}`);
    }
  }

  const getCurrentList = () => {
    return activeTab === 'teams' ? teams : squads;
  };

  return (
    <div className="card boat-config-card-compact">
      <div className="boat-config-header-compact">
        <div>
          <h2 className="h2" style={{ marginTop: 0, marginBottom: 4 }}>Team & Squad Configuration</h2>
          <p className="help" style={{ margin: 0 }}>
            Manage teams and squads for organizing athletes
          </p>
        </div>
        {loading && <span className="help">Loading…</span>}
        {error && <span className="help boat-config-error">{error}</span>}
      </div>

      <div className="boat-config-form-compact">
        <select
          className="input"
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          style={{ minWidth: 100 }}
        >
          <option value="team">Team</option>
          <option value="squad">Squad</option>
        </select>

        <input
          className="input"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          placeholder={selectedType === 'team' ? 'e.g., Varsity, JV, Novice' : 'e.g., Port, Starboard, Sculling'}
          onKeyDown={(e) => e.key === 'Enter' && addOption()}
          style={{ flex: 1, minWidth: 150 }}
        />

        {selectedType === 'team' && (
          <input
            type="color"
            value={newColor}
            onChange={(e) => setNewColor(e.target.value)}
            title="Team color"
            style={{ width: 50, height: 36, cursor: 'pointer', border: '1px solid #ccc', borderRadius: 4 }}
          />
        )}

        <button className="btn" onClick={addOption} style={{ whiteSpace: 'nowrap' }}>
          + Add
        </button>
      </div>

      <div className="boat-config-tabs">
        <button
          className={`boat-config-tab ${activeTab === 'teams' ? 'active' : ''}`}
          onClick={() => setActiveTab('teams')}
        >
          Teams ({teams.length})
        </button>
        <button
          className={`boat-config-tab ${activeTab === 'squads' ? 'active' : ''}`}
          onClick={() => setActiveTab('squads')}
        >
          Squads ({squads.length})
        </button>
      </div>

      <div className="boat-config-tab-content">
        {getCurrentList().length === 0 ? (
          <p className="help" style={{ textAlign: 'center', padding: 16 }}>
            No {activeTab} configured yet. Add some above!
          </p>
        ) : (
          <ul className="list" style={{ margin: 0 }}>
            {getCurrentList().map((item) => (
              <li key={item.id} className="boat-config-item-compact">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {activeTab === 'teams' && (
                    <input
                      type="color"
                      value={item.color || '#3b82f6'}
                      onChange={(e) => updateTeamColor(item.id, e.target.value)}
                      title="Team color"
                      style={{ width: 30, height: 30, cursor: 'pointer', border: '1px solid #ccc', borderRadius: 4 }}
                    />
                  )}
                  <span>{item.name}</span>
                  {activeTab === 'teams' && (
                    <input
                      className="input"
                      type="text"
                      defaultValue={item.slack_channel || ''}
                      placeholder="slack-channel-name"
                      onBlur={(e) => {
                        const trimmed = e.target.value.trim();
                        if (trimmed !== (item.slack_channel || '')) updateTeamSlackChannel(item.id, trimmed);
                      }}
                      style={{ width: 180 }}
                    />
                  )}
                </div>
                <button
                  type="button"
                  className="btn-icon danger"
                  onClick={() => deleteOption(item.id, activeTab === 'teams' ? 'team' : 'squad', item.name)}
                  title={`Delete ${activeTab === 'teams' ? 'team' : 'squad'}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="boat-config-hint-compact">
        <div className="boat-config-hint-content">
          <p className="help" style={{ margin: 0 }}>
            <strong>Teams:</strong> Used to group athletes by skill level (e.g., Varsity, JV, Novice)
            <br />
            <strong>Squads:</strong> Used for practice groupings (e.g., Port, Starboard, Sculling)
            <br />
            <strong>Slack Channel:</strong> The channel name (without #) that practice notes, cancellations, and lineup publish notes for this team will be posted to. Leave blank to disable Slack notifications for this team.
          </p>
        </div>
      </div>

      <div className="boat-config-hint-compact" style={{ marginTop: 8, borderLeft: '3px solid #f59e0b' }}>
        <div className="boat-config-hint-content">
          <p className="help" style={{ margin: 0, color: '#f59e0b' }}>
            <strong>Important:</strong> Any new Team or Squad values added here must also be added to Salesforce to keep data in sync. Contact your Salesforce administrator to update the corresponding picklist fields.
          </p>
        </div>
      </div>
    </div>
  );
}
